import * as React from "react";
import { createRouter, useRouter } from "@tanstack/react-router";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import {
  buildErrorDiagnostics,
  copyErrorDiagnostics,
  stashErrorDiagnostics,
} from "@/lib/errorDiagnostics";

const SERVER_FN_AUTO_RETRY_WINDOW_MS = 10_000;

/**
 * Detects errors thrown from createServerFn handlers that returned a raw Response
 * (e.g. 401 Unauthorized, 403 Forbidden). They surface in the client as the
 * literal string "[object Response]" because Response objects don't stringify nicely.
 * Auto-retry once via router.invalidate() + query invalidation — usually fixes
 * transient auth/session timing issues without forcing a full page reload.
 */
function isServerFnResponseError(error: unknown) {
  if (!error) return false;
  if (typeof Response !== "undefined" && error instanceof Response) return true;
  const message = error instanceof Error ? error.message : String(error);
  return /\[object Response\]/i.test(message);
}

function getServerFnRetryKey() {
  if (typeof window === "undefined") return null;
  return `lecmo:server-fn-retry:${window.location.pathname}`;
}

function DefaultErrorComponent({ error: rawError, reset }: { error: unknown; reset: () => void }) {
  const error = rawError instanceof Error ? rawError : new Error(String(rawError));
  const router = useRouter();
  const queryClient = useQueryClient();
  const isResponseError = isServerFnResponseError(error);
  const [autoRetryPending, setAutoRetryPending] = React.useState(isResponseError);

  // A failed or throttled automatic attempt must expose the manual recovery UI.
  React.useEffect(() => {
    if (!isResponseError || typeof window === "undefined") {
      setAutoRetryPending(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const retryKey = getServerFnRetryKey();
        if (!retryKey) return;
        // Storage can throw in restricted browsers; that also ends the spinner.
        const lastAttemptAt = Number(window.sessionStorage.getItem(retryKey) ?? "0");
        if (Date.now() - lastAttemptAt < SERVER_FN_AUTO_RETRY_WINDOW_MS) return;
        window.sessionStorage.setItem(retryKey, String(Date.now()));
        await queryClient.invalidateQueries();
        if (cancelled) return;
        await router.invalidate({ sync: true });
        if (!cancelled) reset();
      } catch {
        /* fall through to manual UI */
      } finally {
        if (!cancelled) setAutoRetryPending(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isResponseError, queryClient, router, reset]);

  // التقاط تلقائيّ لبيانات التشخيص (المسار + Breadcrumbs + الخطأ) عند render
  const [copied, setCopied] = React.useState(false);
  const diagnostics = React.useMemo(() => buildErrorDiagnostics(error), [error]);
  React.useEffect(() => {
    if (isResponseError) return;
    stashErrorDiagnostics(diagnostics);
    void copyErrorDiagnostics(diagnostics).then((ok) => {
      if (ok) setCopied(true);
    });
  }, [isResponseError, diagnostics]);

  if (isResponseError && autoRetryPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <p className="t-body-sm text-muted-foreground">جاري إعادة المحاولة…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="t-h2 text-foreground">حدث خلل مؤقّت</h1>
        <p className="mt-2 t-body-sm text-muted-foreground">
          تعذّر عرض هذه الصفحة. حاول مجدّداً أو انسخ بيانات التشخيص وأرسلها للدعم.
        </p>
        {import.meta.env.DEV && error.message && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-muted p-3 text-left font-mono t-caption text-destructive">
            {error.message}
          </pre>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="border border-primary px-4 py-2 t-caption uppercase tracking-[var(--tracking-wider)] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            حاول مجدّداً
          </button>
          <button
            onClick={async () => {
              const ok = await copyErrorDiagnostics(diagnostics);
              if (ok) setCopied(true);
            }}
            className="border border-border px-4 py-2 t-caption uppercase tracking-[var(--tracking-wider)] text-foreground transition-colors hover:bg-secondary"
          >
            {copied ? "✓ نُسخ التشخيص" : "نسخ بيانات التشخيص"}
          </button>
          <a
            href="/"
            className="border border-border px-4 py-2 t-caption uppercase tracking-[var(--tracking-wider)] text-foreground transition-colors hover:bg-secondary"
          >
            الرئيسيّة
          </a>
        </div>
      </div>
    </div>
  );
}

export const getRouter = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // 30 minutes — products & content rarely change mid-session.
        // Mutations (cart, auth) call queryClient.invalidateQueries explicitly.
        staleTime: 1000 * 60 * 30,
        gcTime: 1000 * 60 * 60, // 1 hour in memory
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 1,
      },
    },
  });
  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: DefaultErrorComponent,
  });
  return router;
};

